import { useCallback } from 'react';

import type {
  BackgroundTask,
  BackgroundTaskState,
} from './asyncBackendTaskTypes';
import type {
  RunBackgroundTaskFnResponse,
  UseBackgroundTaskOptions,
} from './useBackgroundTask';
import { useBackgroundTask } from './useBackgroundTask';

type CreateBackgroundTaskHookResponse<TResult, TStep extends string, TProps> = (
  options?: UseBackgroundTaskOptions,
) => [
  BackgroundTaskState<TResult, TStep> | undefined,
  (props: TProps) => RunBackgroundTaskFnResponse<TResult>,
];

export function createBackgroundTaskHook<TResult, TStep extends string, TProps>(
  fn: (props: TProps) => Promise<{ task: BackgroundTask<TResult, TStep> }>,
): CreateBackgroundTaskHookResponse<TResult, TStep, TProps> {
  return (options: UseBackgroundTaskOptions = {}) => {
    const { backgroundTaskState, runBackgroundTask } = useBackgroundTask<
      TResult,
      TStep
    >(options);

    const run = useCallback(
      async (props: TProps): RunBackgroundTaskFnResponse<TResult> =>
        runBackgroundTask(() => fn(props)),
      [runBackgroundTask],
    );

    return [backgroundTaskState, run];
  };
}
